import flow from 'lodash/fp/flow';
import map from 'lodash/fp/map';
import upperFirst from 'lodash/fp/upperFirst';
import React from 'react';
import { ClassValue } from 'classnames/types';
import wrapEachComponentWithKey from '../../shared/utils/wrapEachComponentWithKey/wrapEachComponentWithKey';
import Div from '../Element/Div/Div';
import Flex from '../Flex/Flex';
import styles from './GutterBag.module.scss';
import getResponsiveClasses, {
  ResponsiveSelector,
} from '../../shared/utils/getResponsiveClasses/getResponsiveClasses';
import { SupportedGutterSize } from '../Gutter/Gutter';
import { CreateProps } from '../../types/utils/CreateProps';

const getGutterBagItem = (hideOverflow: boolean, growEqually: boolean) => (
  content: React.ReactNode
) => {
  const classes = [
    styles.gutterBag_item,
    {
      [styles.gutterBag_itemGrow]: growEqually,
    },
  ];
  return (
    <Div className={classes} hideOverflow={hideOverflow}>
      {content}
    </Div>
  );
};

type SpacingProps = {
  size?: SupportedGutterSize;
  horizontal?: SupportedGutterSize;
  vertical?: SupportedGutterSize;
};

const getSpacingClasses = ({ size, horizontal, vertical }: SpacingProps) => {
  const classes: string[] = [];
  if (horizontal) {
    classes.push(
      styles[`gutterBag__horizontalGutter${upperFirst(horizontal)}`]
    );
  }
  if (vertical) {
    classes.push(styles[`gutterBag__verticalGutter${upperFirst(vertical)}`]);
  }
  if (size) {
    classes.push(styles[`gutterBag__gutter${upperFirst(size)}`]);
  }
  return classes;
};

export type GutterBagProps = CreateProps<
  {
    /** Array of content that will be presented in GutterBag items */
    contents?: React.ReactElement[];
    /** Size of spacing between content items */
    spacing?: SpacingProps;
    /** Make items full width in specific breakpoints */
    responsiveFullWidth?: ResponsiveSelector;
    /** Should only be used to make the drop shadows of the GutterBag content items visible. */
    showOverflow?: boolean;
    /** Make item equally growable*/
    growEqually?: boolean;
    /** GutterBag class name */
    className?: ClassValue;
  },
  typeof Flex,
  'wrapContent'
>;
// Alias for backwards compatibility
export type GutterBagAttributes = GutterBagProps;

const GutterBag: React.FunctionComponent<GutterBagProps> = ({
  contents,
  spacing,
  className,
  responsiveFullWidth,
  showOverflow,
  growEqually = false,
  ...props
}) => {
  const classes: string[] = [];
  if (spacing) {
    classes.push(...getSpacingClasses(spacing));
  }
  if (responsiveFullWidth) {
    classes.push(
      ...getResponsiveClasses({
        styles,
        prefix: 'gutterBag__fullWidth_',
        selector: responsiveFullWidth,
      })
    );
  }

  return (
    <Div hideOverflow={!showOverflow}>
      <Flex
        wrapContent
        className={[styles.gutterBag, className, ...classes]}
        {...props}
      >
        {flow(
          map(getGutterBagItem(!showOverflow, growEqually)),
          wrapEachComponentWithKey
        )(contents)}
      </Flex>
    </Div>
  );
};

export default GutterBag;
